<!-- components/MediaModal.vue -->
<template>
  <div v-if="modelValue" class="modal-overlay">
    <div class="modal-content">
      <h5 class="mb-3">
        {{
          mode === "edit" ? currentLang.btnEditMedia : currentLang.btnAddMedia
        }}
      </h5>

      <!-- Title -->
      <div class="mb-3">
        <label class="form-label">{{ currentLang.labelTitle }}</label>
        <input
          v-model.trim="title"
          class="form-control"
          :placeholder="currentLang.placeholderTitle"
        />
      </div>

      <!-- Type -->
      <div class="mb-3">
        <label class="form-label">{{ currentLang.labelType }}</label>
        <select v-model="type" class="form-select">
          <option value="" disabled>{{ currentLang.placeholderType }}</option>
          <option value="clip">{{ currentLang.typeClip }}</option>
          <option value="audio">{{ currentLang.typeAudio }}</option>
          <option value="game">{{ currentLang.typeGame }}</option>
        </select>
      </div>

      <!-- Link -->
      <div class="mb-3">
        <label class="form-label">{{ currentLang.labelLink }}</label>
        <input
          v-model.trim="link"
          class="form-control"
          :placeholder="currentLang.placeholderLink"
        />
      </div>

      <div class="mt-3 d-flex gap-2 justify-content-end">
        <button class="btn btn-secondary" @click="close">
          {{ currentLang.back }}
        </button>
        <button class="btn btn-primary" @click="save" :disabled="submitting">
          <span v-if="submitting">{{ currentLang.saving }}</span>
          <span v-else>{{ currentLang.save }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import api from "@/plugins/axios";
import { lang } from "@/plugins/lang";
import { alertStore } from "@/stores/alertStore";

import enText from "@/locales/en/chapters";
import faText from "@/locales/fa/chapters";

const translations = { en: enText, fa: faText };
const currentLang = computed(() => translations[lang.value]);

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  mode: { type: String, default: "create" }, // 'create' | 'edit'
  targetMedia: { type: Object, default: null },
  chapterId: { type: [String, Number], required: true },
});

const emit = defineEmits(["update:modelValue", "refresh"]);

const title = ref("");
const type = ref("");
const link = ref("");
const submitting = ref(false);

// وقتی مدال باز می‌شود و mode/targetMedia تغییر می‌کند
watch(
  () => [props.targetMedia, props.mode, props.modelValue],
  ([media, mode, open]) => {
    if (!open) return;
    if (mode === "edit" && media) {
      title.value = media.title || "";
      type.value = media.type || "";
      link.value = media.link || "";
    } else {
      title.value = "";
      type.value = "";
      link.value = "";
    }
  },
  { immediate: true }
);

const close = () => {
  emit("update:modelValue", false);
};

const save = async () => {
  if (!title.value || !type.value) {
    alertStore.trigger(
      "error",
      currentLang.value.error,
      currentLang.value.validationEmpty
    );
    return;
  }

  submitting.value = true;

  try {
    if (props.mode === "edit" && props.targetMedia) {
      const response = await api.patch(
        `/admin/products/chapter/${props.targetMedia.id}/media`,
        {
          title: title.value,
          type: type.value,
          link: link.value,
        }
      );

      alertStore.trigger(
        "success",
        currentLang.value.success,
        response.data.message
      );
    } else {
      const response = await api.post(
        `/admin/products/chapter/${props.chapterId}/media`,
        {
          title: title.value,
          type: type.value,
          link: link.value,
        }
      );
      alertStore.trigger(
        "success",
        currentLang.value.success,
        response.data.message
      );
    }

    emit("refresh");
    close();
  } catch (e) {
    alertStore.trigger(
      "error",
      currentLang.value.error,
      currentLang.value.generalError
    );
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: #fff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  min-width: 320px;
  max-width: 420px;
}
</style>
